import { Link } from "react-router-dom"
import {
  IconProductHunt,
  IconReddit,
  IconYoutube,
  IconInteriorAI,
} from "../../components/Icons"
import SimpleBeforeAfter from "../../components/SimpleBeforeAfter"
import { useState, useEffect } from "react"
import { Helmet } from "react-helmet-async"

export default function Hero({ keyword }) {
  const isAnime = keyword === "anime-image-upscaling"
  const [showFloatingBar, setShowFloatingBar] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      const windowHeight = window.innerHeight
      const pageHeight = document.documentElement.scrollHeight

      const showThreshold = windowHeight * 0.25
      const hideThreshold = pageHeight - windowHeight * 1.25

      if (scrollPosition > showThreshold && scrollPosition < hideThreshold) {
        setShowFloatingBar(true)
      } else {
        setShowFloatingBar(false)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const title = isAnime
    ? ["Upscale and enhance your anime images with", "AI Magic"]
    : ["Upscale and enhance your images with", "AI Magic"]
  const description = isAnime
    ? "Clarity AI is a high-resolution upscaler for anime and manga images. It enhances character details and improves image quality while preserving the unique art style."
    : "Clarity AI is a high-resolution upscaler that can enhance images and add details. You can decide how much detail you want the AI to add."
  const buttonText = isAnime ? "Upscale Anime Now →" : "Start upscaling now →"

  const comparisonImages = isAnime
    ? {
        inputImage: "https://r2.clarityai.co/assets/spirit_before.avif",
        outputImage: "https://r2.clarityai.co/assets/spirit_after.avif",
        altOutput: "AI Enhanced Anime Image",
        altInput: "Original Anime Image",
        mode: "upscale",
      }
    : {
        outputImage: "https://r2.clarityai.co/assets/bird_after.avif",
        outputImageSmall:
          "https://r2.clarityai.co/assets/bird_after_small.avif",
        outputImageMobile:
          "https://r2.clarityai.co/assets/bird_after_small_mobile.avif",
        inputImage: "https://r2.clarityai.co/assets/bird_before.avif",
        altOutput: "AI Enhanced Image",
        altInput: "Original Image",
        mode: "upscale",
      }

  // Preload LCP image for mobile (non-anime)
  const preloadLCP = !isAnime ? (
    <Helmet>
      <link
        rel="preload"
        as="image"
        href="https://r2.clarityai.co/assets/bird_after_small_mobile.avif"
        imagesrcset="https://r2.clarityai.co/assets/bird_after_small_mobile.avif 640w, https://r2.clarityai.co/assets/bird_after_small.avif 1024w, https://r2.clarityai.co/assets/bird_after.avif 2048w"
        imagesizes="100vw"
        fetchpriority="high"
      />
      <style>{`
        .aspect-ratio-container {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          border-radius: 0.75rem;
        }
        .aspect-ratio-container img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      `}</style>
    </Helmet>
  ) : null

  return (
    <>
      {preloadLCP}
      <div className="w-full flex flex-col items-center justify-center lg:mt-20 bg-bg-color text-white">
        <div className="w-11/12 2xl:w-10/12 flex flex-col md:gap-16 md:flex-row">
          <div className="flex flex-col justify-center mt-10 lg:mt-0 ">
            <h1 className="font-bold text-white text-4xl sm:text-6xl">
              {title[0]}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r to-emerald-600 from-sky-400">
                {title[1]}
              </span>
            </h1>
            <div className="mb-10">
              <h2 className="mb-6 text-md sm:text-lg mt-5 font-medium text-white">
                {description}
              </h2>
              <Link to="/dashboard">
                <button
                  type="submit"
                  className="w-52 h-12 self-center rounded-lg font-bold text-white bg-gradient-to-r via-blue-500  to-emerald-600 from-sky-400 hover:from-black hover:border hover:border-white hover:bg-black hover:text-white background-animate"
                >
                  {buttonText}
                </button>
              </Link>
              <span className="flex text-sm mt-2">
                ✨ <span className="font-bold">29+ Million&nbsp;</span>
                upscales
              </span>
            </div>
          </div>

          <div className="mt-12 lg:mt-0 w-full">
            <div
              className="aspect-ratio-container"
              style={{ aspectRatio: isAnime ? "512/373" : "1/1" }}
            >
              <SimpleBeforeAfter
                beforeImage={comparisonImages.inputImage}
                afterImage={
                  window.innerWidth < 768
                    ? comparisonImages.outputImageMobile
                    : window.innerWidth < 1024
                      ? comparisonImages.outputImageSmall
                      : comparisonImages.outputImage
                }
                beforeAlt={comparisonImages.altInput}
                afterAlt={comparisonImages.altOutput}
                mode={comparisonImages.mode}
                showModeOnly={true}
                priority={true}
              />
            </div>
          </div>
        </div>

        <div className="w-11/12 2xl:w-10/12 flex flex-col text-center">
          <div className="flex flex-wrap items-center gap-1 justify-center content-center mt-12 md:mt-20 text-gray-500">
            <span className="font-semibold text-sm mr-2">Featured on</span>
            <div className="mr-5">
              <IconYoutube />
            </div>
            <div className="mr-5">
              <IconProductHunt />
            </div>
            <div className="flex items-center mr-5">
              <svg
                className="w-8 md:w-9 fill-base-content saturate-0 contrast-50 opacity-80"
                viewBox="0 0 252 252"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_177_29)">
                  <path
                    d="M126 250.793C195.036 250.793 251 194.875 251 125.897C251 56.9181 195.036 1 126 1C56.9644 1 1 56.9181 1 125.897C1 194.875 56.9644 250.793 126 250.793Z"
                    fill="black"
                    stroke="white"
                    strokeMiterlimit="10"
                  ></path>
                  <path
                    d="M48.9999 53.5352L108.748 133.357L48.6233 198.256H62.1561L114.797 141.435L157.327 198.256H203.377L140.265 113.945L196.23 53.5352H182.697L134.219 105.865L95.0494 53.5352H48.9999ZM68.9004 63.4941H90.0554L183.474 188.297H162.319L68.9004 63.4941Z"
                    fill="white"
                  ></path>
                </g>
                <defs>
                  <clipPath id="clip0_177_29">
                    <rect width="252" height="252" fill="white"></rect>
                  </clipPath>
                </defs>
              </svg>
              <span className="ml-1">X/Twitter</span>
            </div>

            <div className="flex items-center mr-5">
              <div className="flex items-center mr-1 justify-center w-8 h-8 bg-gray-500 rounded-full">
                <IconInteriorAI />
              </div>

              <span className="font-extrabold">INTERIOR AI</span>
            </div>

            <div className="">
              <IconReddit />
            </div>
          </div>
        </div>
      </div>

      {showFloatingBar && (
        <Link to="/dashboard">
          <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-emerald-600 p-4 transform transition-transform duration-300 z-50 cursor-pointer hover:brightness-110">
            <div className="container mx-auto flex justify-between items-center px-4">
              <div className="text-white">
                <p className="font-bold text-lg">Join 50k+ other users</p>
                <p className="text-sm">Start upscaling your images today</p>
              </div>
              <button
                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition"
                aria-label="Start Upscaling"
              >
                Start Upscaling now →
              </button>
            </div>
          </div>
        </Link>
      )}
    </>
  )
}
